// Onboarding: Features & Benefits preview screen.
//
// Inserted between the intro slides and the quiz on native iOS/Android
// only (Capacitor). Not shown on web because the marketing landing
// already covers the same ground. Data lives in
// `/app/frontend/src/lib/onboardingPreviewData.js` so this component
// stays presentation-only.
//
// The primary CTA is a "floating" pill that stays fixed at the bottom
// of the viewport at all times — user can tap it any moment to skip
// straight ahead. A soft white-to-transparent gradient sits above it
// so scrolling text never clashes visually with the button.

import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { FEATURES_ONBOARD, toneClasses } from "@/lib/onboardingPreviewData";

const COPY = {
  ar: {
    eyebrow: "المميزات والفوائد",
    title: "كل ما تحتاجه في تطبيق واحد",
    sub: "15 ميزة صُنعت لتجعل رحلتك أسهل — بدون تعقيد، بدون تشتيت.",
    continue: "متابعة",
  },
  en: {
    eyebrow: "Features & Benefits",
    title: "Everything you need in one app",
    sub: "15 features built to make your journey simple — no clutter, no distractions.",
    continue: "Continue",
  },
};

export default function OnboardingFeaturesScreen({ lang = "ar", onContinue }) {
  const isRTL = lang === "ar";
  const t = COPY[lang];

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="always-light fixed inset-0 z-[90] bg-neutral-50 flex flex-col"
      data-testid="onboarding-features-screen"
    >
      {/* Header — brand-orange gradient band that anchors the eyebrow
          + title so the scroll list feels rooted in the app identity. */}
      <div
        className="relative px-5 pb-5 shadow-sm"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 20px)",
          background:
            "linear-gradient(180deg, #FFF7EE 0%, #FFEBD4 60%, #FFFFFF 100%)",
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-500 text-white">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
          </div>
          <span className="text-[11px] font-black uppercase tracking-widest text-brand-600">
            {t.eyebrow}
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-neutral-900 leading-tight">
          {t.title}
        </h1>
        <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{t.sub}</p>
      </div>

      {/* Scrollable feature cards. `pb-32` reserves room for the
          floating CTA so the last card is never hidden behind it. */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-32">
        <div className="max-w-md mx-auto space-y-3">
          {FEATURES_ONBOARD.map((f, i) => {
            const Icon = f.icon;
            const tone = toneClasses(f.tone);
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.3) }}
                className="rounded-2xl bg-white border border-neutral-200/70 p-4 shadow-sm"
                data-testid={`feature-card-${i}`}
              >
                <div className={`flex items-start gap-3 ${isRTL ? "flex-row" : "flex-row"}`}>
                  <div
                    className={`w-11 h-11 rounded-xl ${tone.bg} ${tone.text} ring-1 ${tone.ring} flex items-center justify-center shrink-0`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-neutral-900 leading-snug text-[15px]">
                      {isRTL ? f.title_ar : f.title_en}
                    </h3>
                    <p className="text-[13px] text-neutral-600 mt-1 leading-relaxed">
                      {isRTL ? f.desc_ar : f.desc_en}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Floating Continue CTA — gradient fade above the button so the
          last card doesn't butt into it visually. */}
      <div
        className="absolute inset-x-0 bottom-0 z-10 pointer-events-none"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)",
          paddingTop: "24px",
          background:
            "linear-gradient(180deg, rgba(250,250,250,0) 0%, rgba(250,250,250,0.85) 40%, rgba(250,250,250,1) 100%)",
        }}
      >
        <div className="max-w-md mx-auto px-5 pointer-events-auto">
          <button
            type="button"
            onClick={onContinue}
            className="w-full orange-gradient text-white font-black py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2"
            data-testid="onboarding-features-continue"
          >
            {t.continue}
            {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
